import type { ReportsMessageId } from "./reports.fr";

/** Signalements in English. Same keys and {placeholders} as reports.fr.ts. */
export const reportsEn: Record<ReportsMessageId, string> = {
  "reports.filter.severity": "Severity",
  "reports.filter.status": "Status",
  "reports.filter.all": "All",
  "reports.filter.reviewOverdue": "Suspension review overdue",
  "reports.list.loading": "Loading reports…",
  "reports.list.loadFailed": "Couldn't load the reports.",
  "reports.list.empty": "No reports match these filters.",
  "reports.list.aria": "Reports",
  "reports.list.by": "reported by {name}",
  "reports.list.aboutSubject": "{count} reports about this person",
  "reports.pick": "Pick a report from the list.",
  "reports.loading": "Loading the report…",
  "reports.loadFailed": "Couldn't load this report.",
  "reports.notFound": "This report can't be found. Reload the list.",
  "reports.refreshFailed": "The report couldn't be refreshed: it's shown as it was.",
  "reports.dismiss": "Close",

  "reports.severity.critical": "Critical",
  "reports.severity.high": "High",
  "reports.severity.medium": "Medium",
  "reports.severity.low": "Low",
  "reports.status.open": "Open",
  "reports.status.investigating": "Under investigation",
  "reports.status.decided": "Decided",
  "reports.status.appealed": "Under appeal",
  "reports.status.closed": "Closed",
  "reports.verdict.none": "No penalty",
  "reports.verdict.warning": "Warning",
  "reports.verdict.temporary_suspension": "Temporary suspension",
  "reports.verdict.permanent_ban": "Permanent ban",
  "reports.appeal.upheld": "Decision upheld",
  "reports.appeal.overturned": "Decision overturned",
  "reports.source.app": "From the app",
  "reports.source.phone": "By phone",
  "reports.source.console": "Opened by the team",

  "reports.category.assault": "Assault",

  "reports.new.title": "New report",

  "reports.new.source": "How it reached us",

  "reports.new.byPhone": "By phone",

  "reports.new.byStaff": "Staff noticed it",

  "reports.new.subject": "Who is being reported",

  "reports.new.reporter": "Who reported it",

  "reports.new.reporterHint":
    "Their user id, when a customer reported it. Leave empty if staff noticed it or the caller is not a customer.",

  "reports.new.category": "What happened",

  "reports.new.categoryHint": "The severity follows from this. Change it afterwards if it turns out to be worse.",

  "reports.new.description": "What they told us",

  "reports.new.descriptionHint": "In their words where you can. This is read by whoever investigates.",

  "reports.new.submit": "Open the report",

  "reports.new.needSubject": "Choose who is being reported.",

  "reports.new.needDescription": "Describe what happened, in a sentence at least.",

  "reports.new": "New report",
  "reports.category.sexual_violence": "Sexual violence",
  "reports.category.serious_theft": "Serious theft",
  "reports.category.weapon_threat": "Threat with a weapon",
  "reports.category.theft": "Theft",
  "reports.category.threats": "Threats",
  "reports.category.harassment": "Harassment",
  "reports.category.fraud": "Fraud",
  "reports.category.unsafe_work": "Unsafe work",
  "reports.category.no_show": "No-show",
  "reports.category.other": "Other",

  "reports.pill.suspended": "Suspended",
  "reports.pill.reviewOverdue": "Review overdue",
  "reports.pill.legalHold": "Legal hold",

  "reports.detail.versionTitle": "The version of the report on screen: every action sends it.",
  "reports.detail.critical": "Critical report",
  "reports.detail.needsPolice": "It hasn't been referred to the police yet. The owners have been alerted.",
  "reports.detail.subject": "Reported person",
  "reports.detail.reporter": "Reported by",
  "reports.detail.openedBy": "Opened by {name}",
  "reports.detail.noReporter": "Unknown",
  "reports.detail.job": "Related request",
  "reports.detail.case": "Original case",
  "reports.detail.subjectCase": "Reported person's case",
  "reports.detail.openCase": "Open the case →",
  "reports.detail.noSubjectCase": "None: one opens on suspension or appeal",
  "reports.detail.none": "None",
  "reports.detail.description": "What happened",
  "reports.detail.restrictions": "Suspensions and bans",
  "reports.detail.restrictionsHint":
    "A suspension lasts until a staff decision. The review deadline is not an end: once it passes, the person stays suspended and the review is overdue.",
  "reports.detail.verdict": "Ruling",
  "reports.detail.decidedBy": "By {name}, on {date}",
  "reports.detail.appeal": "Appeal",
  "reports.detail.appealedAt": "Appeal received on {date}",
  "reports.detail.appealPending": "To decide",
  "reports.detail.police": "Police referrals",
  "reports.detail.shared": "Data handed over: {categories} · {name}, {date}",
  "reports.detail.holds": "Legal holds",
  "reports.detail.holdPlaced": "In place since {date} · {name}",
  "reports.detail.holdReleased": "Released on {date} · {name}",
  "reports.detail.related": "Other reports about this person",
  "reports.detail.timeline": "Timeline",
  "reports.detail.actions": "Available actions",
  "reports.detail.noActions": "Your roles allow no action on this report.",

  "reports.restriction.interim_suspension": "Suspension during investigation",
  "reports.restriction.temporary_suspension": "Temporary suspension",
  "reports.restriction.permanent_ban": "Permanent ban",
  "reports.restriction.untilDecision": "Until a staff decision",
  "reports.restriction.untilDecisionReviewBy": "Until a staff decision · review by {date}",
  "reports.restriction.until": "Until {date}",
  "reports.restriction.permanent": "Permanent, unless an appeal succeeds",
  "reports.restriction.switchedOff": "Switched off: {features}",
  "reports.restriction.lifted": "Lifted on {date} by {name}",
  "reports.restriction.inForce": "In force",
  "reports.restriction.ended": "Ended",

  "reports.entry.report": "Report",
  "reports.entry.evidence": "Evidence",
  "reports.entry.severity_changed": "Severity changed",
  "reports.entry.suspended": "Suspended",
  "reports.entry.suspension_lifted": "Suspension lifted",
  "reports.entry.verdict": "Ruling",
  "reports.entry.appeal": "Appeal",
  "reports.entry.appeal_decided": "Appeal decided",
  "reports.entry.police_referral": "Referred to the police",
  "reports.entry.police_data_shared": "Data handed to the police",
  "reports.entry.legal_hold_placed": "Legal hold placed",
  "reports.entry.legal_hold_released": "Legal hold released",
  "reports.entry.linked": "Link added",
  "reports.entry.suspension_withheld": "Suspension not applied",
  "reports.entry.system": "System",
  "reports.entry.evidenceLink": "See the evidence →",

  "reports.police.category.identity": "Identity",
  "reports.police.category.contact_details": "Contact details",
  "reports.police.category.id_document": "ID document",
  "reports.police.category.messages": "Chat messages",
  "reports.police.category.job_details": "Request details",
  "reports.police.category.report_and_evidence": "Report and evidence",
  "reports.police.category.audit_log": "Audit log",
  "reports.police.category.payments": "Payments",
  "reports.hold.entity.safety_report": "This report",
  "reports.hold.entity.user": "The account",
  "reports.hold.entity.job": "The request",
  "reports.hold.entity.conversation": "The conversation",

  "reports.block.ownVerdict": "You made the ruling: someone else must decide the appeal.",
  "reports.block.ownerOnly": "Only an owner can hand data over to the police.",
  "reports.block.noReferral": "Refer the report to the police first, then record the data handed over.",

  "reports.action.evidence": "Add evidence",
  "reports.action.severity": "Change severity",
  "reports.action.suspend": "Suspend during investigation",
  "reports.action.lift": "Lift the suspension",
  "reports.action.verdict": "Make a ruling",
  "reports.action.ban": "Ban permanently",
  "reports.action.appeal": "Decide the appeal",
  "reports.action.police": "Refer to the police",
  "reports.action.share": "Record data handed over",
  "reports.action.hold": "Place a legal hold",
  "reports.action.release": "Release the legal hold",

  "reports.dialog.evidence.title": "Add evidence · {reference}",
  "reports.dialog.evidence.body":
    "Describe the evidence and, if it's online, give its link. It's added to the timeline.",
  "reports.dialog.severity.title": "Change severity · {reference}",
  "reports.dialog.severity.body":
    "A critical report alerts the owners and must be referred to the police. Say why you're changing it.",
  "reports.dialog.suspend.title": "Suspend · {reference}",
  "reports.dialog.suspend.body":
    "{name} can no longer use Walliyo, except to contact the team and appeal, until a staff decision. A review deadline applies: it is not the end of the suspension. The person is notified in the app and a case opens in their name.",
  "reports.dialog.lift.title": "Lift the suspension · {reference}",
  "reports.dialog.lift.body": "{name} gets access to Walliyo back right away. Say why.",
  "reports.dialog.verdict.title": "Make a ruling · {reference}",
  "reports.dialog.verdict.body":
    "{name} reads the reason in the app and can appeal once. No one is penalized without a written reason.",
  "reports.dialog.ban.title": "Ban permanently · {reference}",
  "reports.dialog.ban.body":
    "{name} loses access to Walliyo for good, and their ID document is banned. The reason is shown to them in the app; they can appeal once.",
  "reports.dialog.appeal.title": "Decide the appeal · {reference}",
  "reports.dialog.appeal.body":
    "Overturned lifts whatever the ruling put in place. {name} reads your reason in the app. Someone other than the person who made the ruling must decide.",
  "reports.dialog.police.title": "Refer to the police · {reference}",
  "reports.dialog.police.body": "Record who the report was referred to. Nothing is sent from here.",
  "reports.dialog.share.title": "Data handed to the police · {reference}",
  "reports.dialog.share.body":
    "Record exactly what was handed over, category by category. This record is kept and appears in the audit log.",
  "reports.dialog.hold.title": "Place a legal hold · {reference}",
  "reports.dialog.hold.body":
    "Nothing related to this report can be deleted while the hold is in place, not even by the retention rule.",
  "reports.dialog.release.title": "Release the legal hold · {reference}",
  "reports.dialog.release.body": "Retention rules apply again to everything the hold protected.",

  "reports.field.evidence": "The evidence",
  "reports.field.evidenceUrl": "Link (optional)",
  "reports.field.evidenceUrlHint": "An https:// address, for example a photo uploaded to the case.",
  "reports.field.severity": "New severity",
  "reports.field.verdict": "Ruling",
  "reports.field.suspendedUntil": "Suspended until",
  "reports.field.toldReason": "Reason shown to the person",
  "reports.field.toldHint": "A real sentence, in words the person understands. At least 10 characters.",
  "reports.field.decision": "Decision",
  "reports.field.station": "Police station or unit",
  "reports.field.officerName": "Officer's name",
  "reports.field.officerContact": "Officer's contact (optional)",
  "reports.field.policeReference": "Police reference (optional)",
  "reports.field.referral": "Referral",
  "reports.field.categories": "Data handed over",
  "reports.field.shareDescription": "Exactly what was handed over",
  "reports.field.shareDescriptionHint": "For example: messages from May 3 to 5, scan of the ID card.",
  "reports.field.reasonOptional": "Reason (optional)",

  "reports.problem.reason": "Give a reason of {min} to {max} characters.",
  "reports.problem.told": "Write the reason shown to the person: at least {min} characters.",
  "reports.problem.evidence": "Describe the evidence.",
  "reports.problem.url": "The link must be a full http:// or https:// address.",
  "reports.problem.until": "Pick a suspension end date in the future.",
  "reports.problem.police": "Enter the police station and the officer's name (2 to 200 characters each).",
  "reports.problem.referral": "Pick the referral this concerns.",
  "reports.problem.categories": "Tick at least one category of data handed over.",
  "reports.problem.shareDescription": "Say exactly what was handed over.",

  "reports.done.evidence": "Evidence added to {reference}.",
  "reports.done.severity": "Severity of {reference} changed.",
  "reports.done.suspend": "Person suspended until a staff decision ({reference}). They've been notified in the app.",
  "reports.done.lift": "Suspension lifted ({reference}).",
  "reports.done.verdict": "Ruling saved on {reference}. The person has been notified in the app.",
  "reports.done.ban": "Ban saved on {reference}. The person has been notified in the app.",
  "reports.done.appeal": "Appeal decided on {reference}. The person has been notified in the app.",
  "reports.done.police": "Police referral recorded on {reference}.",
  "reports.done.share": "Data handed over recorded on {reference}.",
  "reports.done.hold": "Legal hold placed on {reference}.",
  "reports.done.release": "Legal hold released on {reference}.",

  "reports.error.stale": "This report changed while you were looking at it. Reload it, check, then try again.",
  "reports.error.refused": "Action refused: your roles don't include the feature needed. Ask a manager for access.",
  "reports.error.appealRefused":
    "Refused: the appeal must be decided by someone other than the person who made the ruling, with HANDLE_APPEALS.",
  "reports.error.shareRefused": "Refused: only an owner can hand data over to the police.",
  "reports.error.invalid":
    "Refused: the report is no longer in the expected state, or a field is invalid. Reload it and check.",
  "reports.error.outcomeUnknown":
    "No answer from the server: the action may have been saved. Try again: the same click can't be applied twice.",

  "reports.refused.wrongStatus":
    "Refused: this report is no longer at that step, so the action no longer applies. Reload it.",
  "reports.refused.alreadyRestricted": "Refused: this person is already suspended by another report.",
  "reports.refused.notSuspended": "Refused: this report has no suspension in force any more. Reload it.",
  "reports.refused.suspendedUntilRequired": "A temporary suspension needs an end date in the future.",
  "reports.refused.ownVerdict": "Refused: you made this ruling, so someone else must decide the appeal.",
  "reports.refused.alreadyHeld": "This report is already under a legal hold. Reload it.",
  "reports.refused.notHeld": "This report is no longer under a legal hold. Reload it.",
};
